// src/pages/Reports/IdleWindowsReportPage.js
//
// Idle Windows Report UI
// ============================================================================
// PURPOSE
// -------
// This page presents weak / underused opening windows based on completed and
// checked visit rows.
//
// MAIN BUSINESS QUESTIONS
// -----------------------
// - Which weekday-slot windows are the weakest?
// - Which windows are frequently open but often commercially empty?
// - Which windows have weak business minutes?
// - Which windows have weak revenue?
// - Which windows look actionable for promotion, shortening, or review?
// - Which windows are not truly idle, but are consumed by technical blocking?
//
// WHY THIS PAGE IS IMPORTANT
// --------------------------
// Slot Performance is excellent for seeing the broad slot picture.
// Idle Windows is intentionally the "problem finder" report.
//
// It helps management focus on:
// - weak periods
// - unstable periods
// - likely schedule inefficiencies
// - windows that may deserve intervention
//
// IMPORTANT MODEL EVOLUTION
// -------------------------
// The backend now distinguishes three different slot-day states:
//
// 1) business_active
//    - real client-service activity touched the slot
//
// 2) technical_blocked
//    - no business activity touched the slot
//    - but technical calendar blocking such as PRZERWA did
//
// 3) idle
//    - neither business work nor technical blocking touched the slot
//
// Therefore:
// - PRZERWA ≠ idle
// - PRZERWA ≠ business active
// - PRZERWA = technical blocked
//
// CURRENT BACKEND SHAPE
// ---------------------
// {
//   period,
//   filters,
//   methodology,
//   opening_schedule,
//   summary,
//   rows,
//   weakest_windows,
//   candidate_windows_for_action
// }
//
// IMPORTANT CONTRACT NOTE
// -----------------------
// The current backend row fields are now:
//
// - business_activity_rate
// - technical_block_rate
// - idle_day_rate
// - avg_business_active_minutes_per_open_day
// - avg_technical_blocked_minutes_per_open_day
// - avg_idle_minutes_per_open_day
// - avg_revenue_per_open_day
// - revenue_per_booked_hour
// - slot_state_profile
//
// This frontend version is fully aligned with that newer contract.
//
// UI GOALS
// --------
// 1) Keep the page visually aligned with the rest of the reports.
// 2) Make weak windows immediately visible.
// 3) Keep ranking readable without overwhelming the user.
// 4) Show a dedicated "candidate for action" section.
// 5) Surface the recommendation as a management hint.
// 6) Clearly expose business vs technical vs idle interpretations.
//
// I18N NOTE
// ---------
// This page keeps backend slot-state, severity and report-contract codes stable.
// The React layer translates only labels, descriptions, chart legends, table
// headers, empty states and local messages. This keeps the UI multilingual
// without weakening the frontend/backend reporting contract.
//

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import AuthContext from "../../context/AuthContext";
import { fetchUsers } from "../../services/userService";
import { fetchIdleWindowsReport } from "../../services/reportService";

import EvaluationPeriodPicker from "../../components/filters/EvaluationPeriodPicker";
import useEvaluationPeriod from "../../hooks/useEvaluationPeriod";
import { EVALUATION_PERIOD_TYPES } from "../../utils/evaluationPeriodUtils";

import {
  ResponsiveContainer,
  ComposedChart,
  BarChart,
  Bar,
  Line,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  ZAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Cell,
  ReferenceLine,
} from "recharts";

import "./IdleWindowsReportPage.css";

/* -------------------------------------------------------------------------- */
/* Small formatting helpers                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Format money-like values with stable 2-decimal precision.
 */
function formatMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0.00";
  return n.toFixed(2);
}

/**
 * Format minute-like values with 1 decimal place.
 *
 * This is intentionally used for averages and overlap-driven metrics,
 * where decimal precision still has some value.
 */
function formatMinutes(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0.0";
  return n.toFixed(1);
}

/**
 * Format integer-like values for counters.
 */
function formatCount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return String(Math.round(n));
}

/**
 * Generic decimal formatter.
 *
 * Useful for chart tooltips and executive aggregates.
 */
function formatNumber(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0.00";
  return n.toFixed(2);
}

/**
 * Format ratio 0..1 as percentage text.
 *
 * Example:
 *   0.42 -> 42.0%
 */
function formatPercentFromRatio(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0.0%";
  return `${(n * 100).toFixed(1)}%`;
}

/**
 * Convert top_services / top_categories arrays into compact text.
 *
 * Expected input:
 *   [{ name, count }, ...]
 */
function formatTopList(items) {
  if (!Array.isArray(items) || items.length === 0) return "—";
  return items.map((x) => `${x.name} (${x.count})`).join(", ");
}

/**
 * Render backend recommendation text safely.
 *
 * Backend-generated recommendation text is intentionally not translated here,
 * because it is business interpretation returned by the reporting API. The
 * frontend only translates the fallback used when the backend sends no text.
 */
function formatRecommendation(value, t) {
  const raw = String(value || "").trim();

  if (raw) return raw;

  return typeof t === "function"
    ? t("reports:common.noRecommendationAvailable", {
        defaultValue: "No recommendation available.",
      })
    : "No recommendation available.";
}

/**
 * Translate backend weakness severity codes for display only.
 *
 * The raw values remain stable for colors, CSS classes, search and backend
 * contract compatibility.
 */
function getSeverityLabel(severity, t) {
  const raw = String(severity || "").toLowerCase();

  switch (raw) {
    case "critical":
      return t("reports:idleWindows.severity.critical");
    case "high":
      return t("reports:idleWindows.severity.high");
    case "moderate":
      return t("reports:idleWindows.severity.moderate");
    case "low":
      return t("reports:idleWindows.severity.low");
    default:
      return severity || t("reports:common.unknown", { defaultValue: "Unknown" });
  }
}

/**
 * Translate backend slot-state profile codes for display only.
 */
function getSlotStateProfileLabel(profile, t) {
  const raw = String(profile || "").toLowerCase();

  switch (raw) {
    case "mostly_idle":
      return t("reports:idleWindows.stateProfiles.mostlyIdle");
    case "mostly_technical_blocked":
      return t("reports:idleWindows.stateProfiles.mostlyTechnicalBlocked");
    case "mostly_business_active":
      return t("reports:idleWindows.stateProfiles.mostlyBusinessActive");
    case "mixed":
      return t("reports:idleWindows.stateProfiles.mixed");
    default:
      return profile || t("reports:common.unknown", { defaultValue: "Unknown" });
  }
}

/* -------------------------------------------------------------------------- */
/* Chart palette + helpers                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Stable local palette for Idle Windows charts.
 *
 * Meaning:
 * - weakness / idle family: amber-orange
 * - commercial value line: blue
 * - severity colors: light blue -> yellow -> orange -> red
 */
const CHART_COLORS = Object.freeze({
  idle: "#f59e0b",
  idleLight: "#fbbf24",
  revenue: "#2563eb",
  revenueLight: "#60a5fa",
  severityLow: "#93c5fd",
  severityModerate: "#fcd34d",
  severityHigh: "#fb923c",
  severityCritical: "#ef4444",
  technical: "#7c3aed",
  grid: "#e5e7eb",
  axis: "#6b7280",
  tooltipBg: "#ffffff",
  tooltipBorder: "#d1d5db",
});

/**
 * Bubble-size helper for scatter chart.
 *
 * We intentionally keep the radius range bounded,
 * so one extreme row does not destroy chart readability.
 */
function bubbleSizeFromValue(value, minValue, maxValue) {
  const v = Number(value) || 0;
  const min = Number(minValue) || 0;
  const max = Number(maxValue) || 0;

  if (max <= min) return 120;

  const ratio = (v - min) / (max - min);
  return 70 + ratio * 250;
}

/**
 * Severity-to-color mapping for executive charts.
 */
function getSeverityColor(severity) {
  const raw = String(severity || "").toLowerCase();

  if (raw === "critical") return CHART_COLORS.severityCritical;
  if (raw === "high") return CHART_COLORS.severityHigh;
  if (raw === "moderate") return CHART_COLORS.severityModerate;
  return CHART_COLORS.severityLow;
}

/* -------------------------------------------------------------------------- */
/* Tooltip components                                                         */
/* -------------------------------------------------------------------------- */

/**
 * Tooltip for the executive "weakest windows" overview.
 *
 * IMPORTANT
 * ---------
 * This version is fully aligned with the CURRENT backend contract.
 *
 * Old field names that should NOT be used anymore:
 * - activity_rate
 * - empty_day_rate
 * - avg_booked_minutes_per_open_day
 *
 * Current field names:
 * - business_activity_rate
 * - idle_day_rate
 * - avg_business_active_minutes_per_open_day
 */
function IdleWeaknessTooltip({ active, payload, t }) {
  if (!active || !Array.isArray(payload) || payload.length === 0) {
    return null;
  }

  const row = payload?.[0]?.payload;
  if (!row) return null;

  return (
    <div className="chart-tooltip chart-tooltip--rich">
      <div className="chart-tooltip__title">
        {row.weekday_label} • {row.slot_key}
      </div>

      <div className="chart-tooltip__grid">
        <div>
          <span>{t("reports:idleWindows.weaknessScore")}</span>
          <strong>{formatCount(row.weakness_score)}</strong>
        </div>

        <div>
          <span>{t("reports:idleWindows.severityLabel")}</span>
          <strong>{getSeverityLabel(row.idle_severity, t)}</strong>
        </div>

        <div>
          <span>{t("reports:idleWindows.businessActivityRate")}</span>
          <strong>{formatPercentFromRatio(row.business_activity_rate)}</strong>
        </div>

        <div>
          <span>{t("reports:idleWindows.technicalBlockRate")}</span>
          <strong>{formatPercentFromRatio(row.technical_block_rate)}</strong>
        </div>

        <div>
          <span>{t("reports:idleWindows.idleDayRate")}</span>
          <strong>{formatPercentFromRatio(row.idle_day_rate)}</strong>
        </div>

        <div>
          <span>{t("reports:idleWindows.stateProfile")}</span>
          <strong>{getSlotStateProfileLabel(row.slot_state_profile, t)}</strong>
        </div>

        <div>
          <span>{t("reports:idleWindows.avgBusinessPerDay")}</span>
          <strong>{formatMinutes(row.avg_business_active_minutes_per_open_day)} min</strong>
        </div>

        <div>
          <span>{t("reports:idleWindows.avgTechnicalPerDay")}</span>
          <strong>{formatMinutes(row.avg_technical_blocked_minutes_per_open_day)} min</strong>
        </div>

        <div>
          <span>{t("reports:idleWindows.avgIdlePerDay")}</span>
          <strong>{formatMinutes(row.avg_idle_minutes_per_open_day)} min</strong>
        </div>

        <div>
          <span>{t("reports:idleWindows.avgRevenuePerDay")}</span>
          <strong>{formatMoney(row.avg_revenue_per_open_day)} PLN</strong>
        </div>
      </div>

      <div className="chart-tooltip__explanation">
        {formatRecommendation(row.recommendation, t)}
      </div>
    </div>
  );
}

/**
 * Tooltip for idle-risk scatter chart.
 *
 * Also fully aligned with the current backend field names.
 */
function IdleScatterTooltip({ active, payload, t }) {
  if (!active || !Array.isArray(payload) || payload.length === 0) {
    return null;
  }

  const row = payload?.[0]?.payload;
  if (!row) return null;

  return (
    <div className="chart-tooltip chart-tooltip--rich">
      <div className="chart-tooltip__title">
        {row.weekday_label} • {row.slot_key}
      </div>

      <div className="chart-tooltip__grid">
        <div>
          <span>{t("reports:idleWindows.weaknessScore")}</span>
          <strong>{formatCount(row.weakness_score)}</strong>
        </div>

        <div>
          <span>{t("reports:idleWindows.avgRevenuePerDay")}</span>
          <strong>{formatMoney(row.avg_revenue_per_open_day)} PLN</strong>
        </div>

        <div>
          <span>{t("reports:idleWindows.avgBusinessPerDay")}</span>
          <strong>{formatMinutes(row.avg_business_active_minutes_per_open_day)} min</strong>
        </div>

        <div>
          <span>{t("reports:idleWindows.avgTechnicalPerDay")}</span>
          <strong>{formatMinutes(row.avg_technical_blocked_minutes_per_open_day)} min</strong>
        </div>

        <div>
          <span>{t("reports:idleWindows.avgIdlePerDay")}</span>
          <strong>{formatMinutes(row.avg_idle_minutes_per_open_day)} min</strong>
        </div>

        <div>
          <span>{t("reports:idleWindows.businessActivityRate")}</span>
          <strong>{formatPercentFromRatio(row.business_activity_rate)}</strong>
        </div>

        <div>
          <span>{t("reports:idleWindows.idleDayRate")}</span>
          <strong>{formatPercentFromRatio(row.idle_day_rate)}</strong>
        </div>

        <div>
          <span>{t("reports:idleWindows.openDays")}</span>
          <strong>{formatCount(row.open_days_count)}</strong>
        </div>

        <div>
          <span>{t("reports:idleWindows.stateProfile")}</span>
          <strong>{getSlotStateProfileLabel(row.slot_state_profile, t)}</strong>
        </div>

        <div>
          <span>{t("reports:idleWindows.severityLabel")}</span>
          <strong>{getSeverityLabel(row.idle_severity, t)}</strong>
        </div>
      </div>

      <div className="chart-tooltip__explanation">
        {formatRecommendation(row.recommendation, t)}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Visual badge helpers                                                       */
/* -------------------------------------------------------------------------- */

function severityClassName(severity) {
  const raw = String(severity || "").toLowerCase();

  if (raw === "critical") return "severity-badge severity-badge--critical";
  if (raw === "high") return "severity-badge severity-badge--high";
  if (raw === "moderate") return "severity-badge severity-badge--moderate";
  return "severity-badge severity-badge--low";
}

function slotStateProfileClassName(profile) {
  const raw = String(profile || "").toLowerCase();

  if (raw === "mostly_idle") {
    return "state-badge state-badge--idle";
  }

  if (raw === "mostly_technical_blocked") {
    return "state-badge state-badge--technical";
  }

  if (raw === "mostly_business_active") {
    return "state-badge state-badge--business";
  }

  return "state-badge state-badge--mixed";
}

function resolveMethodologyNotes(methodology, t) {
  /**
   * Backend methodology text migration helper.
   *
   * The backend now sends stable methodology.note_keys so the frontend can own
   * the visible language through reports.json. Legacy methodology.notes remain
   * supported as a backward-compatible fallback while the report endpoints are
   * migrated incrementally.
   */
  const reportKind = methodology?.report_kind;

  if (reportKind && Array.isArray(methodology?.note_keys) && methodology.note_keys.length > 0) {
    return methodology.note_keys.map((noteKey) =>
      t(`reports:methodology.${reportKind}.notes.${noteKey}`, {
        defaultValue: noteKey,
      })
    );
  }

  return Array.isArray(methodology?.notes) ? methodology.notes : [];
}

/* -------------------------------------------------------------------------- */
/* Main component                                                             */
/* -------------------------------------------------------------------------- */

const IdleWindowsReportPage = () => {
  const { t } = useTranslation(["common", "reports"]);
  const { authState } = useContext(AuthContext);

  const currentRole = authState?.user?.role || "";
  const currentUserId = authState?.user?.id;

  /**
   * Same evaluation-period policy as the other report pages.
   */
  const allowedEvaluationModes = useMemo(() => {
    if (currentRole === "user") {
      return [EVALUATION_PERIOD_TYPES.MONTH];
    }

    return [
      EVALUATION_PERIOD_TYPES.MONTH,
      EVALUATION_PERIOD_TYPES.YEAR,
      EVALUATION_PERIOD_TYPES.WEEK,
      EVALUATION_PERIOD_TYPES.DAY,
      EVALUATION_PERIOD_TYPES.MANUAL,
    ];
  }, [currentRole, t]);

  /**
   * Shared evaluation period controller.
   */
  const evaluationPeriod = useEvaluationPeriod({
    defaultMode: EVALUATION_PERIOD_TYPES.MONTH,
    initialDate: new Date(),
    allowedModes: allowedEvaluationModes,
  });

  const { from: selectedFrom, to: selectedTo } = evaluationPeriod;

  /**
   * Page-local filters.
   *
   * userIds:
   *   backend-facing employee subset filter
   *
   * searchText:
   *   client-side text search in visible rows
   *
   * showOnlyActionCandidates:
   *   client-side narrowing for management focus
   */
  const [filters, setFilters] = useState(() => ({
    userIds: currentRole === "user" && currentUserId != null ? [currentUserId] : [],
    searchText: "",
    showOnlyActionCandidates: false,
  }));

  const [users, setUsers] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  /**
   * Keep track of the actual loaded period.
   *
   * Same UX pattern as in your other reports:
   * picker changes do not auto-refresh the report.
   */
  const [loadedPeriodMeta, setLoadedPeriodMeta] = useState({
    from: selectedFrom,
    to: selectedTo,
    displayLabel: evaluationPeriod.displayLabel || "",
  });

  /* ------------------------------------------------------------------------ */
  /* Keep userIds aligned for role === "user"                                 */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    if (currentRole === "user" && currentUserId != null) {
      setFilters((prev) => ({
        ...prev,
        userIds: [currentUserId],
      }));
    }
  }, [currentRole, currentUserId]);

  /* ------------------------------------------------------------------------ */
  /* Load users for employee filter                                           */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const userList = await fetchUsers();
        setUsers(Array.isArray(userList) ? userList : []);
      } catch (err) {
        console.error("[IdleWindowsReportPage] ❌ Failed to fetch users:", err);
        setError(t("reports:idleWindows.errors.failedToLoadUsers"));
      }
    };

    if (currentRole !== "user") {
      loadUsers();
    }
  }, [currentRole, t]);

  /**
   * Human-readable fixed user field for plain user role.
   */
  const currentUserDisplay = useMemo(() => {
    const firstName = authState?.user?.first_name || "";
    const lastName = authState?.user?.last_name || "";
    const username = authState?.user?.username || "";

    const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();

    if (fullName && username) return `${fullName} (${username})`;
    if (fullName) return fullName;
    if (username) return username;

    return t("reports:common.currentUser", { defaultValue: "Current user" });
  }, [
    authState?.user?.first_name,
    authState?.user?.last_name,
    authState?.user?.username,
    t,
  ]);

  /* ------------------------------------------------------------------------ */
  /* Fetch report                                                             */
  /* ------------------------------------------------------------------------ */

  const loadReportByRange = useCallback(
    async ({ from, to, userIds, displayLabel }) => {
      if (!from || !to) {
        setError(t("reports:common.invalidEvaluationPeriod"));
        return;
      }

      setLoading(true);
      setError("");

      try {
        const payload = await fetchIdleWindowsReport(from, to, userIds);
        setReport(payload || null);

        setLoadedPeriodMeta({
          from,
          to,
          displayLabel: displayLabel || "",
        });
      } catch (err) {
        console.error("[IdleWindowsReportPage] ❌ Error fetching report:", err);
        setError(t("reports:idleWindows.errors.failedToFetch"));
        setReport(null);
      } finally {
        setLoading(false);
      }
    },
    [t]
  );

  /**
   * Initial automatic load.
   */
  const initialRequestRef = useRef({
    from: selectedFrom,
    to: selectedTo,
    userIds: currentRole === "user" && currentUserId != null ? [currentUserId] : [],
    displayLabel: evaluationPeriod.displayLabel || "",
  });

  useEffect(() => {
    loadReportByRange(initialRequestRef.current);
  }, [loadReportByRange]);

  /* ------------------------------------------------------------------------ */
  /* Derived data                                                             */
  /* ------------------------------------------------------------------------ */

  const summary = report?.summary || {};
  const methodologyNotes = resolveMethodologyNotes(report?.methodology, t);

  const rows = Array.isArray(report?.rows) ? report.rows : [];
  const weakestWindows = Array.isArray(report?.weakest_windows)
    ? report.weakest_windows
    : [];
  const candidateWindowsForAction = Array.isArray(report?.candidate_windows_for_action)
    ? report.candidate_windows_for_action
    : [];

  /**
   * Client-side filtered detailed rows.
   *
   * Search covers:
   * - weekday
   * - slot
   * - state profile
   * - top services
   * - top categories
   * - recommendation
   */
  const visibleRows = useMemo(() => {
    let out = [...rows];

    if (filters.showOnlyActionCandidates) {
      out = out.filter((row) => {
        const severity = String(row.idle_severity || "").toLowerCase();
        return severity === "high" || severity === "critical";
      });
    }

    const searchText = String(filters.searchText || "").trim().toLowerCase();

    if (!searchText) return out;

    return out.filter((row) => {
      const weekday = String(row.weekday_label || "").toLowerCase();
      const slot = String(row.slot_key || "").toLowerCase();
      const profile = String(row.slot_state_profile || "").toLowerCase();
      const topServices = formatTopList(row.top_services).toLowerCase();
      const topCategories = formatTopList(row.top_categories).toLowerCase();
      const recommendation = String(row.recommendation || "").toLowerCase();
      const severity = String(row.idle_severity || "").toLowerCase();
      const translatedSeverity = getSeverityLabel(row.idle_severity, t).toLowerCase();
      const translatedProfile = getSlotStateProfileLabel(
        row.slot_state_profile,
        t
      ).toLowerCase();

      return (
        weekday.includes(searchText) ||
        slot.includes(searchText) ||
        profile.includes(searchText) ||
        severity.includes(searchText) ||
        translatedSeverity.includes(searchText) ||
        translatedProfile.includes(searchText) ||
        topServices.includes(searchText) ||
        topCategories.includes(searchText) ||
        recommendation.includes(searchText)
      );
    });
  }, [rows, filters.showOnlyActionCandidates, filters.searchText, t]);

  /* ------------------------------------------------------------------------ */
  /* Executive chart datasets                                                 */
  /* ------------------------------------------------------------------------ */

  /**
   * 1) Weakness overview chart
   *
   * IMPORTANT
   * ---------
   * We explicitly carry the full row via ...row.
   * This ensures tooltip access to all current backend fields.
   */
  const topWeaknessChartData = useMemo(() => {
    return [...rows]
      .slice(0, 12)
      .map((row) => ({
        ...row,
        label: `${row.weekday_label} • ${row.slot_key}`,
      }))
      .reverse();
  }, [rows]);

  /**
   * 2) Revenue-risk scatter
   *
   * X = avg business-active minutes per open day
   * Y = avg revenue per open day
   * Bubble size = weakness score
   *
   * This chart distinguishes:
   * - low business + low revenue windows
   * - low business but still valuable windows
   * - higher business but weak economics
   */
  const idleScatterData = useMemo(() => {
    if (!rows.length) return [];

    const weaknessValues = rows.map((row) => Number(row.weakness_score || 0));
    const minWeakness = Math.min(...weaknessValues);
    const maxWeakness = Math.max(...weaknessValues);

    return rows.map((row, index) => ({
      ...row,
      id: `${row.scoped_slot_key || row.slot_key || "slot"}-${index}`,
      x: Number(row.avg_business_active_minutes_per_open_day || 0),
      y: Number(row.avg_revenue_per_open_day || 0),
      z: bubbleSizeFromValue(row.weakness_score, minWeakness, maxWeakness),
      color: getSeverityColor(row.idle_severity),
    }));
  }, [rows]);

  /**
   * 3) Weekday rollup
   *
   * Aggregates weekday-scoped slot rows into one weekday summary.
   *
   * IMPORTANT
   * ---------
   * This must also use CURRENT field names:
   * - avg_business_active_minutes_per_open_day
   * - avg_revenue_per_open_day
   * - business_activity_rate
   */
  const weekdayRollupData = useMemo(() => {
    const map = new Map();

    rows.forEach((row) => {
      const key = String(row.weekday_label || "Unknown");

      if (!map.has(key)) {
        map.set(key, {
          weekday_label: key,
          windows_count: 0,
          total_weakness_score: 0,
          total_avg_business_active_minutes_per_open_day: 0,
          total_avg_revenue_per_open_day: 0,
          total_business_activity_rate: 0,
        });
      }

      const agg = map.get(key);

      agg.windows_count += 1;
      agg.total_weakness_score += Number(row.weakness_score || 0);
      agg.total_avg_business_active_minutes_per_open_day += Number(
        row.avg_business_active_minutes_per_open_day || 0
      );
      agg.total_avg_revenue_per_open_day += Number(
        row.avg_revenue_per_open_day || 0
      );
      agg.total_business_activity_rate += Number(row.business_activity_rate || 0);
    });

    const weekdayOrder = [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday",
    ];

    return Array.from(map.values())
      .map((row) => ({
        weekday_label: row.weekday_label,
        avg_weakness_score:
          row.windows_count > 0 ? row.total_weakness_score / row.windows_count : 0,
        avg_business_minutes_per_window:
          row.windows_count > 0
            ? row.total_avg_business_active_minutes_per_open_day / row.windows_count
            : 0,
        avg_revenue_per_window:
          row.windows_count > 0
            ? row.total_avg_revenue_per_open_day / row.windows_count
            : 0,
        avg_business_activity_rate:
          row.windows_count > 0
            ? row.total_business_activity_rate / row.windows_count
            : 0,
        windows_count: row.windows_count,
      }))
      .sort(
        (a, b) =>
          weekdayOrder.indexOf(a.weekday_label) - weekdayOrder.indexOf(b.weekday_label)
      );
  }, [rows]);

  /**
   * 4) Severity distribution
   *
   * A compact executive chart showing how many windows fall into each severity.
   */
  const severityDistributionData = useMemo(() => {
    const counts = {
      low: 0,
      moderate: 0,
      high: 0,
      critical: 0,
    };

    rows.forEach((row) => {
      const severity = String(row.idle_severity || "").toLowerCase();
      if (counts[severity] != null) {
        counts[severity] += 1;
      }
    });

    return [
      {
        label: t("reports:idleWindows.severity.low"),
        value: counts.low,
        color: CHART_COLORS.severityLow,
      },
      {
        label: t("reports:idleWindows.severity.moderate"),
        value: counts.moderate,
        color: CHART_COLORS.severityModerate,
      },
      {
        label: t("reports:idleWindows.severity.high"),
        value: counts.high,
        color: CHART_COLORS.severityHigh,
      },
      {
        label: t("reports:idleWindows.severity.critical"),
        value: counts.critical,
        color: CHART_COLORS.severityCritical,
      },
    ];
  }, [rows, t]);

  const hasReportData = Boolean(report);

  /* ------------------------------------------------------------------------ */
  /* Render                                                                   */
  /* ------------------------------------------------------------------------ */

  return (
    <div className="idle-windows-page">
      <div className="idle-windows-header">
        <div className="header-left">
          <h1>{t("reports:idleWindows.title")}</h1>
          <p className="header-hint">{t("reports:idleWindows.headerHint")}</p>
        </div>
      </div>

      {error && <p className="error-message">{error}</p>}

      <div className="filters-bar">
        <div className="filters-left">
          <EvaluationPeriodPicker period={evaluationPeriod} />

          {currentRole !== "user" ? (
            <div className="filter-item filter-item-users">
              <label className="filter-label">{t("reports:common.employees")}</label>

              <select
                multiple
                className="user-dropdown"
                value={filters.userIds}
                onChange={(e) => {
                  const selectedValues = Array.from(
                    e.target.selectedOptions,
                    (o) => Number(o.value)
                  ).filter(Number.isFinite);

                  setFilters((prev) => ({
                    ...prev,
                    userIds: selectedValues,
                  }));
                }}
              >
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.first_name} {user.last_name} ({user.username})
                  </option>
                ))}
              </select>

              <div className="filter-help">{t("reports:common.multiSelectHelp")}</div>
            </div>
          ) : (
            <div className="filter-item filter-item-users">
              <label className="filter-label">{t("reports:common.employee")}</label>
              <input
                type="text"
                className="user-fixed"
                value={currentUserDisplay}
                readOnly
              />
            </div>
          )}

          <div className="filter-item filter-item-search">
            <label className="filter-label">{t("reports:common.search")}</label>
            <input
              type="text"
              className="search-input"
              value={filters.searchText}
              placeholder={t("reports:idleWindows.searchPlaceholder")}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  searchText: e.target.value,
                }))
              }
            />
          </div>

          <div className="filter-item filter-item-toggle">
            <label className="filter-label">{t("reports:idleWindows.focus")}</label>
            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={filters.showOnlyActionCandidates}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    showOnlyActionCandidates: e.target.checked,
                  }))
                }
              />
              <span>{t("reports:idleWindows.showOnlyActionCandidates")}</span>
            </label>
          </div>
        </div>

        <div className="filters-actions">
          <button
            className="apply-button"
            onClick={() =>
              loadReportByRange({
                from: selectedFrom,
                to: selectedTo,
                userIds: filters.userIds,
                displayLabel: evaluationPeriod.displayLabel,
              })
            }
            disabled={loading}
          >
            {loading ? t("reports:common.loading") : t("reports:common.generate")}
          </button>
        </div>
      </div>

      <div className="report-scroll">
        {loading ? (
          <div className="loading-panel">{t("reports:common.loading")}</div>
        ) : hasReportData ? (
          <>
            {/* -------------------------------------------------------------- */}
            {/* Summary cards                                                 */}
            {/* -------------------------------------------------------------- */}
            <div className="summary-grid">
              <div className="summary-card">
                <span className="summary-label">{t("reports:idleWindows.analyzedDays")}</span>
                <span className="summary-value">
                  {formatCount(summary.analyzed_days_total)}
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:idleWindows.loadedRows")}</span>
                <span className="summary-value">
                  {formatCount(summary.completed_rows_count)}
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:idleWindows.businessActiveMinutes")}</span>
                <span className="summary-value">
                  {formatMinutes(summary.total_business_active_minutes)}
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:idleWindows.technicalBlockedMinutes")}</span>
                <span className="summary-value">
                  {formatMinutes(summary.total_technical_blocked_minutes)}
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:idleWindows.idleMinutes")}</span>
                <span className="summary-value">
                  {formatMinutes(summary.total_idle_minutes)}
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:idleWindows.criticalWindows")}</span>
                <span className="summary-value">
                  {formatCount(summary.critical_windows_count)}
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:idleWindows.highWeaknessWindows")}</span>
                <span className="summary-value">
                  {formatCount(summary.high_windows_count)}
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:idleWindows.actionCandidates")}</span>
                <span className="summary-value">
                  {formatCount(summary.candidate_windows_for_action_count)}
                </span>
              </div>
            </div>

            {/* -------------------------------------------------------------- */}
            {/* Highlight cards                                               */}
            {/* -------------------------------------------------------------- */}
            <div className="highlight-grid">
              <div className="highlight-card">
                <div className="highlight-title">{t("reports:idleWindows.weakestByBusinessActivity")}</div>
                {summary?.weakest_by_business_window ? (
                  <>
                    <div className="highlight-name">
                      {summary.weakest_by_business_window.weekday_label} •{" "}
                      {summary.weakest_by_business_window.slot_key}
                    </div>
                    <div className="highlight-metric">
                      {formatMinutes(
                        summary.weakest_by_business_window.avg_business_active_minutes_per_open_day
                      )}{" "}
                      {t("reports:idleWindows.minPerDay")}
                    </div>
                  </>
                ) : (
                  <div className="highlight-empty">{t("reports:idleWindows.noData")}</div>
                )}
              </div>

              <div className="highlight-card">
                <div className="highlight-title">{t("reports:idleWindows.weakestByRevenue")}</div>
                {summary?.weakest_by_revenue_window ? (
                  <>
                    <div className="highlight-name">
                      {summary.weakest_by_revenue_window.weekday_label} •{" "}
                      {summary.weakest_by_revenue_window.slot_key}
                    </div>
                    <div className="highlight-metric">
                      {formatMoney(summary.weakest_by_revenue_window.avg_revenue_per_open_day)} {t("reports:idleWindows.plnPerDay")}
                    </div>
                  </>
                ) : (
                  <div className="highlight-empty">{t("reports:idleWindows.noData")}</div>
                )}
              </div>

              <div className="highlight-card">
                <div className="highlight-title">{t("reports:idleWindows.mostTechnicalBlockedWindow")}</div>
                {summary?.most_technical_blocked_window ? (
                  <>
                    <div className="highlight-name">
                      {summary.most_technical_blocked_window.weekday_label} •{" "}
                      {summary.most_technical_blocked_window.slot_key}
                    </div>
                    <div className="highlight-metric">
                      {formatMinutes(
                        summary.most_technical_blocked_window.avg_technical_blocked_minutes_per_open_day
                      )}{" "}
                      {t("reports:idleWindows.techMinPerDay")}
                    </div>
                  </>
                ) : (
                  <div className="highlight-empty">{t("reports:idleWindows.noData")}</div>
                )}
              </div>
            </div>

            {/* -------------------------------------------------------------- */}
            {/* Methodology                                                   */}
            {/* -------------------------------------------------------------- */}
            <div className="methodology-card">
              <div className="methodology-title">{t("reports:idleWindows.methodology")}</div>
              <ul className="methodology-list">
                {methodologyNotes.map((note, idx) => (
                  <li key={idx}>{note}</li>
                ))}
              </ul>

              {(loadedPeriodMeta?.from || loadedPeriodMeta?.to) && (
                <div className="loaded-period-note">
                  {t("reports:common.loadedPeriod")}: <strong>{loadedPeriodMeta.from}</strong> →{" "}
                  <strong>{loadedPeriodMeta.to}</strong>
                  {loadedPeriodMeta.displayLabel ? (
                    <> ({loadedPeriodMeta.displayLabel})</>
                  ) : null}
                </div>
              )}
            </div>

            {/* -------------------------------------------------------------- */}
            {/* Chart section                                                 */}
            {/* -------------------------------------------------------------- */}
            <div className="charts-grid">
              <div className="report-card chart-card chart-card--wide">
                <div className="card-header">
                  <h2>{t("reports:idleWindows.weakestWindowsOverviewTitle")}</h2>
                  <p>{t("reports:idleWindows.weakestWindowsOverviewDescription")}</p>
                </div>

                <div className="chart-container chart-container--large">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      data={topWeaknessChartData}
                      layout="vertical"
                      margin={{ top: 10, right: 24, left: 16, bottom: 8 }}
                    >
                      <CartesianGrid stroke={CHART_COLORS.grid} strokeDasharray="3 3" />

                      <XAxis
                        type="number"
                        yAxisId="left"
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                      />

                      <XAxis
                        type="number"
                        xAxisId="top"
                        orientation="top"
                        hide
                      />

                      <YAxis
                        type="category"
                        dataKey="label"
                        width={150}
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                      />

                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        hide
                      />

                      <Tooltip content={<IdleWeaknessTooltip t={t} />} />
                      <Legend />

                      <Bar
                        xAxisId="top"
                        dataKey="weakness_score"
                        name={t("reports:idleWindows.weaknessScore")}
                        fill={CHART_COLORS.idle}
                        radius={[0, 8, 8, 0]}
                      />

                      <Line
                        xAxisId="left"
                        dataKey="avg_revenue_per_open_day"
                        name={t("reports:idleWindows.avgRevenuePerDay")}
                        stroke={CHART_COLORS.revenue}
                        strokeWidth={2.5}
                        dot={{ r: 2, fill: CHART_COLORS.revenue }}
                        activeDot={{ r: 5, fill: CHART_COLORS.revenue }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="report-card chart-card">
                <div className="card-header">
                  <h2>{t("reports:idleWindows.idleRiskMatrixTitle")}</h2>
                  <p>{t("reports:idleWindows.idleRiskMatrixDescription")}</p>
                </div>

                <div className="chart-container chart-container--large">
                  <ResponsiveContainer width="100%" height="100%">
                    <ScatterChart margin={{ top: 10, right: 24, bottom: 12, left: 8 }}>
                      <CartesianGrid stroke={CHART_COLORS.grid} strokeDasharray="3 3" />

                      <XAxis
                        type="number"
                        dataKey="x"
                        name={t("reports:idleWindows.avgBusinessPerDay")}
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                        label={{
                          value: t("reports:idleWindows.avgBusinessActiveMinutesPerOpenDay"),
                          position: "insideBottom",
                          offset: -4,
                          fill: CHART_COLORS.axis,
                        }}
                      />

                      <YAxis
                        type="number"
                        dataKey="y"
                        name={t("reports:idleWindows.avgRevenuePerDay")}
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                        label={{
                          value: t("reports:idleWindows.avgRevenuePerOpenDayPln"),
                          angle: -90,
                          position: "insideLeft",
                          fill: CHART_COLORS.axis,
                        }}
                      />

                      <ZAxis type="number" dataKey="z" range={[70, 320]} />
                      <Tooltip content={<IdleScatterTooltip t={t} />} />
                      <Legend />

                      <ReferenceLine
                        x={20}
                        stroke="#cbd5e1"
                        strokeDasharray="4 4"
                        ifOverflow="extendDomain"
                      />

                      <ReferenceLine
                        y={80}
                        stroke="#cbd5e1"
                        strokeDasharray="4 4"
                        ifOverflow="extendDomain"
                      />

                      <Scatter name={t("reports:idleWindows.idleWindows")} data={idleScatterData}>
                        {idleScatterData.map((entry) => (
                          <Cell key={`idle-dot-${entry.id}`} fill={entry.color} />
                        ))}
                      </Scatter>
                    </ScatterChart>
                  </ResponsiveContainer>
                </div>

                <div className="chart-legend-note">
                  <span className="legend-chip legend-chip--low">{t("reports:idleWindows.severity.low")}</span>
                  <span className="legend-chip legend-chip--moderate">{t("reports:idleWindows.severity.moderate")}</span>
                  <span className="legend-chip legend-chip--high">{t("reports:idleWindows.severity.high")}</span>
                  <span className="legend-chip legend-chip--critical">{t("reports:idleWindows.severity.critical")}</span>
                </div>
              </div>

              <div className="report-card chart-card">
                <div className="card-header">
                  <h2>{t("reports:idleWindows.weekdayIdleOpportunityTitle")}</h2>
                  <p>{t("reports:idleWindows.weekdayIdleOpportunityDescription")}</p>
                </div>

                <div className="chart-container chart-container--large">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      data={weekdayRollupData}
                      margin={{ top: 10, right: 24, left: 0, bottom: 8 }}
                    >
                      <CartesianGrid stroke={CHART_COLORS.grid} strokeDasharray="3 3" />

                      <XAxis
                        dataKey="weekday_label"
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                      />

                      <YAxis
                        yAxisId="left"
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                      />

                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        domain={[0, 1]}
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                        tickFormatter={(value) => `${Math.round(value * 100)}%`}
                      />

                      <Tooltip
                        formatter={(value, name) => {
                          if (name === t("reports:idleWindows.avgWeaknessScore")) {
                            return [formatNumber(value), name];
                          }

                          if (name === t("reports:idleWindows.avgBusinessActivityRate")) {
                            return [formatPercentFromRatio(value), name];
                          }

                          if (name === t("reports:idleWindows.avgRevenuePerWindow")) {
                            return [`${formatMoney(value)} PLN`, name];
                          }

                          if (name === t("reports:idleWindows.avgBusinessPerWindow")) {
                            return [`${formatMinutes(value)} min`, name];
                          }

                          return [value, name];
                        }}
                        contentStyle={{
                          backgroundColor: CHART_COLORS.tooltipBg,
                          border: `1px solid ${CHART_COLORS.tooltipBorder}`,
                          borderRadius: 8,
                        }}
                      />

                      <Legend />

                      <Bar
                        yAxisId="left"
                        dataKey="avg_weakness_score"
                        name={t("reports:idleWindows.avgWeaknessScore")}
                        fill={CHART_COLORS.idle}
                        radius={[6, 6, 0, 0]}
                      />

                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="avg_business_activity_rate"
                        name={t("reports:idleWindows.avgBusinessActivityRate")}
                        stroke={CHART_COLORS.revenue}
                        strokeWidth={2.5}
                        dot={{ r: 3, fill: CHART_COLORS.revenue }}
                        activeDot={{ r: 5, fill: CHART_COLORS.revenue }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="report-card chart-card">
                <div className="card-header">
                  <h2>{t("reports:idleWindows.severityDistributionTitle")}</h2>
                  <p>{t("reports:idleWindows.severityDistributionDescription")}</p>
                </div>

                <div className="chart-container">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={severityDistributionData}
                      margin={{ top: 10, right: 24, left: 0, bottom: 8 }}
                    >
                      <CartesianGrid stroke={CHART_COLORS.grid} strokeDasharray="3 3" />
                      <XAxis
                        dataKey="label"
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                      />
                      <YAxis tick={{ fill: CHART_COLORS.axis, fontSize: 12 }} />
                      <Tooltip
                        formatter={(value) => [formatCount(value), t("reports:idleWindows.windows")]}
                        contentStyle={{
                          backgroundColor: CHART_COLORS.tooltipBg,
                          border: `1px solid ${CHART_COLORS.tooltipBorder}`,
                          borderRadius: 8,
                        }}
                      />
                      <Legend />
                      <Bar dataKey="value" name={t("reports:idleWindows.windows")} radius={[6, 6, 0, 0]}>
                        {severityDistributionData.map((entry, index) => (
                          <Cell key={`severity-bar-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* -------------------------------------------------------------- */}
            {/* Weakest windows cards                                         */}
            {/* -------------------------------------------------------------- */}
            <div className="report-card">
              <div className="card-header">
                <h2>{t("reports:idleWindows.topWeakestWindowsTitle")}</h2>
                <p>{t("reports:idleWindows.topWeakestWindowsDescription")}</p>
              </div>

              <div className="candidate-grid">
                {weakestWindows.map((row) => (
                  <div className="candidate-card" key={row.scoped_slot_key}>
                    <div className="candidate-card__header">
                      <div className="candidate-card__title">
                        {row.weekday_label} • {row.slot_key}
                      </div>

                      <div className="candidate-card__badges">
                        <span className={severityClassName(row.idle_severity)}>
                          {getSeverityLabel(row.idle_severity, t)}
                        </span>
                        <span className={slotStateProfileClassName(row.slot_state_profile)}>
                          {getSlotStateProfileLabel(row.slot_state_profile, t)}
                        </span>
                      </div>
                    </div>

                    <div className="candidate-card__metrics">
                      <div>
                        <span>{t("reports:idleWindows.weaknessScore")}</span>
                        <strong>{formatCount(row.weakness_score)}</strong>
                      </div>

                      <div>
                        <span>{t("reports:idleWindows.businessActivityRate")}</span>
                        <strong>{formatPercentFromRatio(row.business_activity_rate)}</strong>
                      </div>

                      <div>
                        <span>{t("reports:idleWindows.technicalBlockRate")}</span>
                        <strong>{formatPercentFromRatio(row.technical_block_rate)}</strong>
                      </div>

                      <div>
                        <span>{t("reports:idleWindows.idleDayRate")}</span>
                        <strong>{formatPercentFromRatio(row.idle_day_rate)}</strong>
                      </div>

                      <div>
                        <span>{t("reports:idleWindows.avgBusinessPerDay")}</span>
                        <strong>{formatMinutes(row.avg_business_active_minutes_per_open_day)} min</strong>
                      </div>

                      <div>
                        <span>{t("reports:idleWindows.avgTechnicalPerDay")}</span>
                        <strong>{formatMinutes(row.avg_technical_blocked_minutes_per_open_day)} min</strong>
                      </div>

                      <div>
                        <span>{t("reports:idleWindows.avgIdlePerDay")}</span>
                        <strong>{formatMinutes(row.avg_idle_minutes_per_open_day)} min</strong>
                      </div>

                      <div>
                        <span>{t("reports:idleWindows.avgRevenuePerDay")}</span>
                        <strong>{formatMoney(row.avg_revenue_per_open_day)} PLN</strong>
                      </div>
                    </div>

                    <div className="candidate-card__block">
                      <div className="candidate-card__label">{t("reports:idleWindows.topServices")}</div>
                      <div className="candidate-card__text">{formatTopList(row.top_services)}</div>
                    </div>

                    <div className="candidate-card__block">
                      <div className="candidate-card__label">{t("reports:idleWindows.topCategories")}</div>
                      <div className="candidate-card__text">{formatTopList(row.top_categories)}</div>
                    </div>

                    <div className="candidate-card__block">
                      <div className="candidate-card__label">{t("reports:idleWindows.recommendation")}</div>
                      <div className="candidate-card__text">{formatRecommendation(row.recommendation, t)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* -------------------------------------------------------------- */}
            {/* Detailed table                                                */}
            {/* -------------------------------------------------------------- */}
            <div className="report-card">
              <div className="card-header">
                <h2>{t("reports:idleWindows.detailedRankingTitle")}</h2>
                <p>{t("reports:idleWindows.detailedRankingDescription")}</p>
              </div>

              <div className="table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>{t("reports:idleWindows.weekday")}</th>
                      <th>{t("reports:idleWindows.slot")}</th>
                      <th>{t("reports:idleWindows.severityLabel")}</th>
                      <th>{t("reports:idleWindows.stateProfile")}</th>
                      <th>{t("reports:idleWindows.weaknessScore")}</th>
                      <th>{t("reports:idleWindows.openDays")}</th>
                      <th>{t("reports:idleWindows.businessDays")}</th>
                      <th>{t("reports:idleWindows.technicalOnlyDays")}</th>
                      <th>{t("reports:idleWindows.idleDays")}</th>
                      <th>{t("reports:idleWindows.businessRate")}</th>
                      <th>{t("reports:idleWindows.technicalRate")}</th>
                      <th>{t("reports:idleWindows.idleRate")}</th>
                      <th>{t("reports:idleWindows.avgBusinessPerOpenDay")}</th>
                      <th>{t("reports:idleWindows.avgTechnicalPerOpenDay")}</th>
                      <th>{t("reports:idleWindows.avgIdlePerOpenDay")}</th>
                      <th>{t("reports:idleWindows.avgRevenuePerOpenDay")}</th>
                      <th>{t("reports:idleWindows.revenuePerBookedHour")}</th>
                      <th>{t("reports:idleWindows.businessVisits")}</th>
                      <th>{t("reports:idleWindows.technicalEntries")}</th>
                      <th>{t("reports:idleWindows.topServices")}</th>
                      <th>{t("reports:idleWindows.topCategories")}</th>
                      <th>{t("reports:idleWindows.recommendation")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRows.length > 0 ? (
                      visibleRows.map((row) => (
                        <tr key={row.scoped_slot_key}>
                          <td>{row.weekday_label}</td>
                          <td>{row.slot_key}</td>
                          <td>
                            <span className={severityClassName(row.idle_severity)}>
                              {getSeverityLabel(row.idle_severity, t)}
                            </span>
                          </td>
                          <td>
                            <span className={slotStateProfileClassName(row.slot_state_profile)}>
                              {getSlotStateProfileLabel(row.slot_state_profile, t)}
                            </span>
                          </td>
                          <td>{formatCount(row.weakness_score)}</td>
                          <td>{formatCount(row.open_days_count)}</td>
                          <td>{formatCount(row.days_with_business_activity)}</td>
                          <td>{formatCount(row.days_with_technical_block_only)}</td>
                          <td>{formatCount(row.idle_open_days_count)}</td>
                          <td>{formatPercentFromRatio(row.business_activity_rate)}</td>
                          <td>{formatPercentFromRatio(row.technical_block_rate)}</td>
                          <td>{formatPercentFromRatio(row.idle_day_rate)}</td>
                          <td>{formatMinutes(row.avg_business_active_minutes_per_open_day)}</td>
                          <td>{formatMinutes(row.avg_technical_blocked_minutes_per_open_day)}</td>
                          <td>{formatMinutes(row.avg_idle_minutes_per_open_day)}</td>
                          <td>{formatMoney(row.avg_revenue_per_open_day)} PLN</td>
                          <td>{formatMoney(row.revenue_per_booked_hour)} PLN/h</td>
                          <td>{formatCount(row.total_business_visits)}</td>
                          <td>{formatCount(row.total_technical_entries)}</td>
                          <td className="wrap-cell">{formatTopList(row.top_services)}</td>
                          <td className="wrap-cell">{formatTopList(row.top_categories)}</td>
                          <td className="wrap-cell">{formatRecommendation(row.recommendation, t)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={22} className="empty-table-cell">
                          {t("reports:idleWindows.noIdleWindowsMatchFilters")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* -------------------------------------------------------------- */}
            {/* Action candidates table                                       */}
            {/* -------------------------------------------------------------- */}
            <div className="report-card">
              <div className="card-header">
                <h2>{t("reports:idleWindows.candidateWindowsForActionTitle")}</h2>
                <p>{t("reports:idleWindows.candidateWindowsForActionDescription")}</p>
              </div>

              <div className="table-wrapper">
                <table className="data-table data-table--compact">
                  <thead>
                    <tr>
                      <th>{t("reports:idleWindows.weekday")}</th>
                      <th>{t("reports:idleWindows.slot")}</th>
                      <th>{t("reports:idleWindows.severityLabel")}</th>
                      <th>{t("reports:idleWindows.stateProfile")}</th>
                      <th>{t("reports:idleWindows.weaknessScore")}</th>
                      <th>{t("reports:idleWindows.businessRate")}</th>
                      <th>{t("reports:idleWindows.technicalRate")}</th>
                      <th>{t("reports:idleWindows.idleRate")}</th>
                      <th>{t("reports:idleWindows.avgRevenuePerOpenDay")}</th>
                      <th>{t("reports:idleWindows.recommendation")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {candidateWindowsForAction.length > 0 ? (
                      candidateWindowsForAction.map((row) => (
                        <tr key={`action-${row.scoped_slot_key}`}>
                          <td>{row.weekday_label}</td>
                          <td>{row.slot_key}</td>
                          <td>
                            <span className={severityClassName(row.idle_severity)}>
                              {getSeverityLabel(row.idle_severity, t)}
                            </span>
                          </td>
                          <td>
                            <span className={slotStateProfileClassName(row.slot_state_profile)}>
                              {getSlotStateProfileLabel(row.slot_state_profile, t)}
                            </span>
                          </td>
                          <td>{formatCount(row.weakness_score)}</td>
                          <td>{formatPercentFromRatio(row.business_activity_rate)}</td>
                          <td>{formatPercentFromRatio(row.technical_block_rate)}</td>
                          <td>{formatPercentFromRatio(row.idle_day_rate)}</td>
                          <td>{formatMoney(row.avg_revenue_per_open_day)} PLN</td>
                          <td className="wrap-cell">{formatRecommendation(row.recommendation, t)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={10} className="empty-table-cell">
                          {t("reports:idleWindows.noActionCandidates")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : (
          <div className="empty-panel">{t("reports:common.noDataAvailable")}</div>
        )}
      </div>
    </div>
  );
};

export default IdleWindowsReportPage;

